/**
 * Query keys for Buyer Smart Import.
 *
 * <p>Its own factory rather than a branch of `buyerKeys`, because the two
 * prefixes answer to different writes. A smart-import batch reaching
 * `Completed` creates buyers, so the POLL invalidates `buyerKeys.all` — but
 * nothing that happens to a buyer afterwards changes the batch that created
 * it, and putting the history under the buyer prefix would refetch it on every
 * unrelated buyer write for the rest of the session.</p>
 *
 * <p>The `staleAfterSaveGate` requires every `queryKey` array literal to live
 * in a `*Keys` file, which is what this file is; the reads it registers are
 * listed below.</p>
 */
export const smartImportKeys = {
  all: ["buyer-smart-import"] as const,

  /** The history list (D13). */
  batches: () => [...smartImportKeys.all, "batches"] as const,

  /** One batch, polled while it is Queued or Running (D20). Under the
   *  `batches` prefix so committing or undoing refreshes both with one call. */
  batch: (id: string) => [...smartImportKeys.batches(), id] as const,

  /**
   * The server dry-run behind the review screen (D4).
   *
   * <p>🔴 Keyed on the file's content hash AND a stable signature of the whole
   * approved review state, because "every change re-runs a server dry-run"
   * IS a cache key: flipping a target and flipping it back re-serves the
   * previous answer instead of paying for a second round-trip, and a file
   * swapped under the same review state is a different question with a
   * different key. The file itself cannot be in a key — it is re-sent as a
   * multipart part on every call, and never persisted (D5).</p>
   */
  preview: (contentHash: string, reviewSignature: string) =>
    [...smartImportKeys.all, "preview", contentHash, reviewSignature] as const,

  /**
   * The listings the file names, and what each implies (D8).
   *
   * <p>Keyed on the content hash and a signature of ONLY what the interests
   * call reads — which tab contributes listing titles, and from which column.
   * Keying it on the whole review state instead would pay for a fresh Claude
   * call every time somebody adjusted an unrelated column.</p>
   */
  interests: (contentHash: string, listingSignature: string) =>
    [...smartImportKeys.all, "interests", contentHash, listingSignature] as const,
};
